import {APP_API_BASE as API_BASE} from './appApiConfig';

const TOKEN_KEY = 'vocabmaster_auth_token';

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function withApiBase(path) {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/')) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

function mapWordMedia(word) {
  return {...word, mediaUrl: withApiBase(word.mediaUrl)};
}

async function requestJson(path, options = {}) {
  const token = getAuthToken();
  const authHeaders = token ? {Authorization: `Bearer ${token}`} : {};

  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with status ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

export async function fetchWords({language, orderBy, search = ''}) {
  const query = new URLSearchParams({language, orderBy});
  if (search && search.trim() !== '') query.set('search', search.trim());
  const words = await requestJson(`/api/words?${query.toString()}`);
  return words.map(mapWordMedia);
}

export async function createWord({language, wordEn, wordFa, transliteration, sentences, sourceUrl, sourceTitle}) {
  const created = await requestJson('/api/words', {
    method: 'POST',
    body: JSON.stringify({language, wordEn, wordFa, transliteration, sentences, sourceUrl, sourceTitle}),
  });
  return mapWordMedia(created);
}

export async function bulkCreateWords({language, words}) {
  return requestJson('/api/words/bulk', {
    method: 'POST',
    body: JSON.stringify({language, words}),
  });
}

export async function importUrl({url}) {
  return requestJson('/api/import/url', {
    method: 'POST',
    body: JSON.stringify({url}),
  });
}

export async function updateWord({language, id, wordEn, wordFa, mediaUrl, transliteration, tags, sentences}) {
  const updated = await requestJson(`/api/words/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({language, wordEn, wordFa, mediaUrl, transliteration, tags, sentences}),
  });
  return mapWordMedia(updated);
}

export async function removeWord({language, id}) {
  const query = new URLSearchParams({language});
  return requestJson(`/api/words/${encodeURIComponent(id)}?${query.toString()}`, {method: 'DELETE'});
}

export async function addCategory({language, id, category}) {
  return requestJson(`/api/words/${encodeURIComponent(id)}/categories`, {
    method: 'POST',
    body: JSON.stringify({language, category}),
  });
}

export async function removeCategory({language, id, category}) {
  return requestJson(`/api/words/${encodeURIComponent(id)}/categories`, {
    method: 'DELETE',
    body: JSON.stringify({language, category}),
  });
}

export async function uploadMedia({language, file}) {
  const token = getAuthToken();
  const authHeaders = token ? {Authorization: `Bearer ${token}`} : {};
  const form = new FormData();
  form.append('language', language);
  form.append('file', file);

  const response = await fetch(`${API_BASE}/api/upload`, {
    method: 'POST',
    headers: authHeaders,
    body: form,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Upload failed with status ${response.status}`);
  }

  const body = await response.json();
  return {...body, mediaUrl: withApiBase(body.mediaUrl)};
}

export async function deleteMedia({mediaUrl}) {
  return requestJson('/api/upload', {
    method: 'DELETE',
    body: JSON.stringify({mediaUrl}),
  });
}

export async function fetchStudyProgress({language}) {
  return requestJson(`/api/study/progress?language=${encodeURIComponent(language)}`);
}

export async function saveStudyProgress({wordId, language, correct, grade, responseMs}) {
  const body = {wordId, language};
  if (typeof grade === 'number') {
    body.grade = grade;
  } else {
    body.correct = correct;
  }
  if (typeof responseMs === 'number') body.responseMs = responseMs;
  return requestJson('/api/study/progress', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function fetchDueWords({language, limit}) {
  const query = new URLSearchParams({language});
  if (limit) query.set('limit', String(limit));
  return requestJson(`/api/study/due?${query.toString()}`);
}

export async function fetchStudyStats({language, days}) {
  const query = new URLSearchParams({language});
  if (days) query.set('days', String(days));
  return requestJson(`/api/study/stats?${query.toString()}`);
}
